import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { TFormErrorResponse } from '@shared/types';
import type {
  CandidateResume,
  CandidateResumeActivityResponse,
  ChangeCandidateResumeActivityRequest,
} from '../../types';
import { candidatesApi } from '../candidatesApi';
import { candidateMutationKeys, candidateQueryKeys } from '../queryKeys';

type ChangeCandidateResumeActivityVariables = {
  candidateId: number;
  resumeId: number;
  request: ChangeCandidateResumeActivityRequest;
};

export const useChangeCandidateResumeActivity = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CandidateResumeActivityResponse,
    AxiosError<TFormErrorResponse>,
    ChangeCandidateResumeActivityVariables
  >({
    mutationKey: candidateMutationKeys.changeResumeActivity,
    mutationFn: ({ resumeId, request }) =>
      candidatesApi.changeCandidateResumeActivity(resumeId, request),
    onSuccess: async (activity, { candidateId, resumeId }) => {
      queryClient.setQueryData<CandidateResume>(
        candidateQueryKeys.resume(candidateId, resumeId),
        resume => (resume ? { ...resume, ...activity } : resume)
      );

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: candidateQueryKeys.allResumes(candidateId),
        }),
        queryClient.invalidateQueries({
          queryKey: candidateQueryKeys.detail(candidateId),
          exact: true,
        }),
      ]);
    },
  });
};
